import { useState, useEffect, lazy, Suspense } from "react";
import { db } from "../../firebase";
import { useNavigate, useLocation } from "react-router-dom";
import { collection, getDocs, doc, setDoc, getDoc } from "firebase/firestore";
import "./UpdateMarks.css";

const Navbar = lazy(() => import("./Navbar"));
const NavbarApp = lazy(() => import("./NavbarApp"));

const UpdateMarks = () => {
  const [students, setStudents] = useState([]);
  const [marksData, setMarksData] = useState({});
  const [modules, setModules] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [courseOptions, setCourseOptions] = useState([]);
  const [companyName, setCompanyName] = useState("Loading...");

  const navigate = useNavigate();
  const location = useLocation();

  const NavbarComponent = location.pathname.includes("/app") ? NavbarApp : Navbar;

  const storedCompanyCode =
    location.state?.companyCode || localStorage.getItem("companyCode");

  const [companyCode, setCompanyCode] = useState(storedCompanyCode || "");

  useEffect(() => {
    if (!storedCompanyCode) {
      alert("Company code missing. Please login again.");
      navigate("/");
    } else {
      localStorage.setItem("companyCode", storedCompanyCode);
    }
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const coursesRef = collection(
          db,
          "CorporateClients",
          companyCode,
          "programInfo"
        );

        const snapshot = await getDocs(coursesRef);

        const courses = snapshot.docs.map((doc) => ({
          courseId: doc.id,
        }));

        setCourseOptions(courses);

        if (courses.length > 0) {
          setSelectedCourse(courses[0].courseId);
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
      }
    };

    if (companyCode) {
      fetchCourses();
    }
  }, [companyCode]);

  useEffect(() => {
    if (selectedCourse) fetchModules(selectedCourse);
  }, [selectedCourse]);

  const fetchModules = async (courseId) => {
    try {
      const modulesRef = collection(
        db,
        "CorporateClients",
        companyCode,
        "programInfo",
        courseId,
        "modules"
      );

      const snapshot = await getDocs(modulesRef);

      const moduleList = snapshot.docs.map((doc) => ({
        moduleId: doc.id,
        name: doc.data().name || doc.id,
      }));

      setModules(moduleList);
    } catch (error) {
      console.error("Error fetching modules:", error);
    }
  };

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const studentsRef = collection(
          db,
          "CorporateClients",
          companyCode,
          "studentInfo"
        );

        const snapshot = await getDocs(studentsRef);

        const studentsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })).filter(student => !/trainer|lsm/i.test(student.fullName || ""));

        setStudents(studentsList);
      } catch (error) {
        console.error("Error fetching students:", error);
      }
    };

    if (companyCode) {
      fetchStudents();
    }
  }, [companyCode]);

  useEffect(() => {
    const fetchCompanyName = async () => {
      try {
        const companyRef = doc(db, "CorporateClients", companyCode);
        const companySnap = await getDoc(companyRef);

        if (companySnap.exists()) {
          setCompanyName(companySnap.data().companyName || "Unknown");
        } else {
          setCompanyName("Unknown");
        }
      } catch (error) {
        console.error("Error fetching company name:", error);
        setCompanyName("Error");
      }
    };

    if (companyCode) {
      fetchCompanyName();
    }
  }, [companyCode]);

  useEffect(() => {
    const marks = {};

    students.forEach((student) => {
      const courseMarks = student.marks?.[selectedCourse] || {};

      marks[student.id] = {};

      modules.forEach((module) => {
        const moduleId = module.moduleId;

        marks[student.id][moduleId] = {
          assignment: courseMarks[moduleId]?.assignment || "",
          quiz: courseMarks[moduleId]?.quiz || "",
          comment: courseMarks[moduleId]?.comment || "",
        };
      });
    });

    setMarksData(marks);
  }, [students, selectedCourse, modules]);

  const handleCourseChange = (e) => {
    setSelectedCourse(e.target.value);
    setSelectedModule("");
  };

  const handleMarksChange = (studentId, moduleId, type, value) => {
    setMarksData((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [moduleId]: {
          ...prev[studentId]?.[moduleId],
          [type]: value,
        },
      },
    }));
  };

  const saveAllMarks = async () => {
    try {
      const updates = students.map(async (student) => {
        const studentRef = doc(
          db,
          "CorporateClients",
          companyCode,
          "studentInfo",
          student.id
        );

        const updatedMarks = {
          ...student.marks,
          [selectedCourse]: marksData[student.id] || {},
        };

        return setDoc(
          studentRef,
          {
            fullName: student.fullName,
            email: student.email,
            marks: updatedMarks,
            attendance: student.attendance || {},
          },
          { merge: true }
        );
      });

      await Promise.all(updates);

      alert("All marks updated successfully!");
    } catch (error) {
      console.error("Error updating marks:", error);
      alert("Failed to update marks.");
    }
  };

  return (
    <>
      <Suspense fallback={<div>Loading Navbar...</div>}>
        <NavbarComponent companyName={companyName} />
      </Suspense>

      <div className="update-container">
        <h2>Update Student Marks - {companyCode}</h2>

        <div className="course-select-row">
          <div className="course-select">
            <label>Select Course:</label>
            <select value={selectedCourse} onChange={handleCourseChange}>
              <option value="" disabled>Select Course...</option>
              {courseOptions.map((course) => (
                <option key={course.courseId} value={course.courseId}>
                  {course.courseId}
                </option>
              ))}
            </select>
          </div>

          <div className="course-select">
            <label>Select Module:</label>
            <select
              value={selectedModule}
              onChange={(e) => setSelectedModule(e.target.value)}
              disabled={!selectedCourse}
            >
              <option value="">All Modules</option>
              {modules.map((module) => (
                <option key={module.moduleId} value={module.moduleId}>
                  {module.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedCourse && (
          <>
            <div className="table-wrapper">
              <table className="update-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Course</th>
                    <th>Module</th>
                    <th>Assignment <br/> (Out of 25)</th>
                    <th>Quiz <br/> (Out of 25)</th>
                    <th>Comment</th>
                  </tr>
                </thead>

                <tbody>
                  {[...students]
                    .sort((a, b) => a.fullName.localeCompare(b.fullName))
                    .map((student) => {
                      const studentMarks = marksData[student.id] || {};
                      const displayedModules = selectedModule
                        ? modules.filter((m) => m.moduleId === selectedModule)
                        : modules;
                      const rowCount = displayedModules.length || 1;

                      return displayedModules.map((module, index) => (
                        <tr key={`${student.id}-${module.moduleId}`}>
                          {index === 0 && (
                            <>
                              <td rowSpan={rowCount}>{student.fullName}</td>
                              <td rowSpan={rowCount}>{selectedCourse}</td>
                            </>
                          )}

                          <td>{module.name}</td>

                          <td>
                            <input
                              type="number"
                              value={studentMarks[module.moduleId]?.assignment || ""}
                              onChange={(e) =>
                                handleMarksChange(
                                  student.id,
                                  module.moduleId,
                                  "assignment",
                                  e.target.value
                                )
                              }
                            />
                          </td>

                          <td>
                            <input
                              type="number"
                              value={studentMarks[module.moduleId]?.quiz || ""}
                              onChange={(e) =>
                                handleMarksChange(
                                  student.id,
                                  module.moduleId,
                                  "quiz",
                                  e.target.value
                                )
                              }
                            />
                          </td>

                          <td>
                            <textarea
                              value={studentMarks[module.moduleId]?.comment || ""}
                              onChange={(e) =>
                                handleMarksChange(
                                  student.id,
                                  module.moduleId,
                                  "comment",
                                  e.target.value
                                )
                              }
                            />
                          </td>
                        </tr>
                      ));
                    })}
                </tbody>
              </table>
            </div>
            <div className="save-all-container">
              <button
                className="save-all-btn"
                onClick={saveAllMarks}
              >
                Save All Changes
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default UpdateMarks;
